import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Avatar,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Download as DownloadIcon,
  Assessment as ReportIcon,
  People as PeopleIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { timeTrackingApi } from "../../../services/timetracking-api";
import { TimesheetReport, formatDuration } from "../../../types/timetracking";
import {
  formatDate,
  formatTime,
  formatDateOnly,
} from "../../../services/dateTimeService";

const Reports: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [reports, setReports] = useState<TimesheetReport[]>([]);
  const [summaryReport, setSummaryReport] = useState<TimesheetReport | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  // Modal states
  const [selectedReport, setSelectedReport] = useState<TimesheetReport | null>(
    null,
  );
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const loadReports = async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    try {
      setError(null);
      const [allReports, summary] = await Promise.all([
        timeTrackingApi.getAllTimesheetReports(
          formatDateOnly(startDate),
          formatDateOnly(endDate),
        ),
        timeTrackingApi.getOverallSummaryReport(
          formatDateOnly(startDate),
          formatDateOnly(endDate),
        ),
      ]);

      setReports(allReports);
      setSummaryReport(summary);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("error.failedToLoadReports"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = async () => {
    if (!startDate || !endDate) return;

    try {
      // Get browser timezone
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const csvData = await timeTrackingApi.exportTimesheetDataAsCsv(
        formatDateOnly(startDate),
        formatDateOnly(endDate),
        undefined, // employeeId - undefined for all employees
        browserTimezone, // timezone for local conversion
      );

      // Create and download CSV file
      const blob = new Blob([csvData], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `timesheet_report_${formatDateOnly(startDate)}_to_${formatDateOnly(endDate)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to export data");
    }
  };

  const handleViewDetails = async (report: TimesheetReport) => {
    setSelectedReport(report);
    setDetailsModalOpen(true);
    setDetailsLoading(true);

    try {
      // Fetch detailed report for the employee
      const detailedReport = await timeTrackingApi.getEmployeeTimesheetReport(
        report.employeeId,
        startDate ? formatDateOnly(startDate) : "",
        endDate ? formatDateOnly(endDate) : "",
      );
      setSelectedReport(detailedReport);
    } catch (err) {
      console.error(t("error.failedToLoadEmployeeDetails"), err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedReport(null);
  };

  useEffect(() => {
    loadReports();
  }, [startDate, endDate]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ pt: 0, pr: 2, pb: 2, pl: 2 }}>
        {/* Controls */}
        <Card elevation={1} sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label={t("timetracking.labels.startDate")}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label={t("timetracking.labels.endDate")}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={loadReports}
                  disabled={loading}
                  startIcon={
                    loading ? <CircularProgress size={16} /> : <ReportIcon />
                  }
                >
                  {t("timetracking.reports.generateReport")}
                </Button>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleExportCsv}
                  disabled={loading || reports.length === 0}
                  startIcon={<DownloadIcon />}
                >
                  {t("timetracking.actions.exportData")}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Statistics */}
        {summaryReport && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                      <PeopleIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" color="primary.main">
                        {reports.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("timetracking.reports.totalEmployees")}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: "success.main", mr: 2 }}>
                      <TimeIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" color="success.main">
                        {formatDuration(summaryReport.totalHours)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("timetracking.reports.totalHours")}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: "info.main", mr: 2 }}>
                      <TrendingUpIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" color="info.main">
                        {formatDuration(summaryReport.averageHoursPerDay)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("timetracking.reports.averageHoursPerDay")}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: "secondary.main", mr: 2 }}>
                      <ReportIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" color="secondary.main">
                        {summaryReport.totalDays}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("timetracking.reports.totalWorkingDays")}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Error Display */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={loadReports}>
                {t("common.retry")}
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Reports Table */}
        {!loading && reports.length > 0 && (
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" component="div" gutterBottom>
                {t("timetracking.reports.employeeReports")} ({reports.length})
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size={isMobile ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t("timetracking.labels.employeeName")}
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          {t("timetracking.labels.department")}
                        </TableCell>
                      )}
                      <TableCell>{t("timetracking.totalHours")}</TableCell>
                      <TableCell>
                        {t("timetracking.labels.workingDays")}
                      </TableCell>
                      <TableCell>{t("timetracking.averageHours")}</TableCell>
                      <TableCell>{t("timetracking.reports.actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.map((report) => (
                      <TableRow key={report.employeeId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {report.employeeName}
                          </Typography>
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {report.department || "-"}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            color="primary.main"
                          >
                            {formatDuration(report.totalHours)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {report.totalDays}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDuration(report.averageHoursPerDay)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleViewDetails(report)}
                          >
                            {t("timetracking.reports.viewDetails")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* No Data State */}
        {!loading && reports.length === 0 && !error && (
          <Alert severity="info">{t("timetracking.timesheet.noData")}</Alert>
        )}

        {/* Employee Details Modal */}
        <Dialog
          open={detailsModalOpen}
          onClose={handleCloseDetailsModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedReport?.employeeName} -{" "}
            {t("timetracking.reports.employeeReports")}
          </DialogTitle>
          <DialogContent>
            {detailsLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <CircularProgress />
              </Box>
            ) : selectedReport ? (
              <Box sx={{ pt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{t("timetracking.labels.employeeName")}:</strong>{" "}
                  {selectedReport.employeeName}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{t("timetracking.labels.department")}:</strong>{" "}
                  {selectedReport.department || "-"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{t("timetracking.totalHours")}:</strong>{" "}
                  {formatDuration(selectedReport.totalHours)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{t("timetracking.labels.workingDays")}:</strong>{" "}
                  {selectedReport.totalDays}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{t("timetracking.averageHours")}:</strong>{" "}
                  {formatDuration(selectedReport.averageHoursPerDay)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>{t("timetracking.labels.dateRange")}:</strong>{" "}
                  {formatDate(selectedReport.startDate)} -{" "}
                  {formatDate(selectedReport.endDate)}
                </Typography>

                {selectedReport.timeEntries &&
                  selectedReport.timeEntries.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        {t("timetracking.timesheet.details")}
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                {t("timetracking.labels.date")}
                              </TableCell>
                              <TableCell>
                                {t("timetracking.labels.timeIn")}
                              </TableCell>
                              <TableCell>
                                {t("timetracking.labels.timeOut")}
                              </TableCell>
                              <TableCell>
                                {t("timetracking.totalHours")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedReport.timeEntries.map((entry, idx) => (
                              <TableRow key={idx}>
                                <TableCell>
                                  {formatDate(entry.timeIn)}
                                </TableCell>
                                <TableCell>
                                  {entry.timeIn
                                    ? formatTime(entry.timeIn)
                                    : "-"}
                                </TableCell>
                                <TableCell>
                                  {entry.timeOut
                                    ? formatTime(entry.timeOut)
                                    : "-"}
                                </TableCell>
                                <TableCell>
                                  {formatDuration(entry.totalHours)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetailsModal}>
              {t("common.close")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;
